import { useState, useEffect, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAltTwoTone";
import PersonIcon from "@mui/icons-material/PersonTwoTone";
import RequestQuoteIcon from "@mui/icons-material/RequestQuoteTwoTone";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCarTwoTone";
import AddIcon from "@mui/icons-material/AddTwoTone";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Entities from "../components/Entities";
import CustomerDetails from "../components/CustomerDetails";
import Notes from "../components/Notes";
import {
  saveValue,
  getApplication,
  updateAsset,
  applicationSelector,
  assetSelector,
  loanDetailsSelector,
  saveLoanDetails,
  userDetailsSelector,
  createCustomer,
  clearState,
  removeCustomer,
  updateDeclaration,
  addAdditionalAsset,
  removeAdditionalAsset,
  setRemoveAssetModalOptions,
} from "../store/slices/applicationFormSlice";
import {
  Alert,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
} from "@mui/material";
import _ from "lodash";
import ProductSelector from "../components/ProductSelector";
import LoanDetails from "src/components/Application/LoanDetails";
import { getCustomerRole } from "src/utils/commonFunctions";
import Asset from "src/components/Asset";
import ConfirmModal from "src/components/customComponents/ConfirmModal";
import { maxAssetsLimit, declarationAnswers } from "src/constants";

const Application = memo(({ socket }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();
  const {
    linkSent,
    gotASICData,
    submitSuccess,
    gettingApplication,
    gettingAdditionalAsset,
    removeAssetModalOptions,
  } = useSelector(userDetailsSelector);
  const application = useSelector(applicationSelector);
  const asset = useSelector(assetSelector);
  const loanDetails = useSelector(loanDetailsSelector);
  const declaration = application?.declaration;
  const [declarationOne, setDeclarationOne] = useState(declaration?.one);
  const [declarationTwo, setDeclarationTwo] = useState(declaration?.two);
  const [declarationThree, setDeclarationThree] = useState(declaration?.three);
  const [isApplicationComplete, setIsApplicationComplete] = useState(false);

  const [confirmRemoveCustomer, setConfirmRemoveCustomer] = useState({});
  const customerRole = getCustomerRole(application?.entities);

  function handleApplicationComplete(isComplete) {
    setIsApplicationComplete(isComplete);
  }

  const handleSupplier = (event) => {
    const update = _.merge({
      ...asset,
      ...{ [event.target.name]: event.target.value },
    });
    dispatch(updateAsset(update));
  };

  async function handleApplicationLoad() {
    if (applicationId) await dispatch(getApplication(applicationId)).unwrap();
  }

  function handleAddCustomer() {
    dispatch(
      createCustomer({
        applicationId: application._id,
        entities: application?.entities,
      })
    );
  }

  useEffect(() => {
    setDeclarationOne(declaration?.one);
  }, [declaration?.one]);

  useEffect(() => {
    setDeclarationTwo(declaration?.two);
  }, [declaration]);

  useEffect(() => {
    setDeclarationThree(declaration?.three);
  }, [declaration]);

  useEffect(() => {
    setDeclarationOne(declaration?.one);
    setDeclarationTwo(declaration?.two);
    setDeclarationThree(declaration?.three);
  }, [declaration?.one]);

  useEffect(() => {
    handleApplicationLoad();

    return function cleanUp() {
      dispatch(clearState());
    };
  }, [applicationId]);

  const handle = {
    onChangeLoanDetailsField: (field, value) => {
      dispatch(saveLoanDetails({ ...loanDetails, [field]: value }));
    },
    addAdditionalAsset: (event) => {
      event.stopPropagation();
      dispatch(addAdditionalAsset({ applicationId }));
    },
    closeRemoveAssetModal: () => {
      dispatch(
        setRemoveAssetModalOptions({
          isOpen: false,
          assetType: "",
          assetId: "",
        })
      );
    },
    removeAsset: async (data) => {
      if (data.isSave) {
        // Remove asset from application
        dispatch(
          removeAdditionalAsset({
            assetId: removeAssetModalOptions?.assetId,
            applicationId,
          })
        ).then(() => {
          // Close modal after removing asset
          handle.closeRemoveAssetModal();
        });
      }
    },
  };

  const firstDeclarationStyle = !declarationOne ? { color: "#d32f2f" } : {};
  const secondDeclarationStyle = !declarationTwo ? { color: "#d32f2f" } : {};
  const thirdDeclarationStyle = !declarationThree ? { color: "#d32f2f" } : {};

  const [assetList, setAssetList] = useState([]);

  useEffect(() => {
    if (
      !gettingApplication &&
      asset &&
      typeof asset === "object" &&
      "_id" in asset
    ) {
      const totalAssets = [asset];

      if (
        application?.additionalAssets &&
        application.additionalAssets.length > 0
      ) {
        totalAssets.push(...application.additionalAssets);
      }

      // Fix type: ensure all _id are string via mapping/casting
      setAssetList(
        totalAssets.map((a) => ({
          ...a,
          _id: String(a._id),
        }))
      );
    } else {
      setAssetList([]);
    }
  }, [gettingApplication, asset, application?.additionalAssets]);

  const isMaxAssetsReached = assetList.length >= maxAssetsLimit;
  const isDisabledNewAssetButton = gettingAdditionalAsset || isMaxAssetsReached;

  return (
    <>
      {gettingApplication ? (
        <Grid
          container
          item
          style={{ marginTop: "100px", padding: "30px 0px 100px" }}
        >
          <CircularProgress
            size={40}
            style={{ margin: "auto", display: "block" }}
          />
        </Grid>
      ) : (
        <>
          <Grid
            container
            item
            style={{ marginTop: "64px", padding: "30px 0px 100px" }}
          >
            <Grid
              container
              item
              sm={3}
              md={3}
              lg={3}
              style={{ padding: "0 20px" }}
            >
              <Notes handleApplicationComplete={handleApplicationComplete} />
            </Grid>

            <Grid
              container
              item
              sm={6}
              md={6}
              lg={6}
              style={{ padding: "0 20px" }}
            >
              <Box>
                <Accordion
                  defaultExpanded
                  id="assetDetails"
                  elevation={1}
                  style={{ padding: "0 15px" }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <Grid container item sm={12} md={12} lg={12} spacing={1}>
                      <Grid container item sm={6} md={6} lg={6}>
                        <DirectionsCarIcon style={{ marginRight: "15px" }} />
                        <Typography>Asset Details</Typography>
                      </Grid>
                      <Grid
                        container
                        item
                        sm={6}
                        md={6}
                        lg={6}
                        justifyContent="flex-end"
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          style={{ marginRight: "10px" }}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            disabled={isDisabledNewAssetButton}
                            onClick={handle.addAdditionalAsset}
                          >
                            {gettingAdditionalAsset ? (
                              <CircularProgress size={12} />
                            ) : (
                              <AddIcon />
                            )}
                            {gettingAdditionalAsset ? "Adding..." : "Add Asset"}
                          </Button>
                          {isMaxAssetsReached && (
                            <Tooltip title={`You can add up to ${maxAssetsLimit} assets`} style={{ marginLeft: "10px" }}>
                              <InfoOutlinedIcon fontSize="small" color="primary" />
                            </Tooltip>
                          )}
                        </Stack>
                      </Grid>
                    </Grid>
                  </AccordionSummary>

                  <AccordionDetails style={{ paddingBottom: "40px" }}>
                    {assetList &&
                      assetList.length > 0 &&
                      assetList.map((asset, index) => (
                        <Asset
                          index={index}
                          asset={asset}
                          isMaxAssetsReached={isMaxAssetsReached}
                          loanDetails={loanDetails}
                          applicationType={application?.applicationType || ""}
                          applicationId={applicationId}
                        />
                      ))}
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  defaultExpanded
                  id="loanDetails"
                  elevation={1}
                  style={{ padding: "0 15px" }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel3a-content"
                    id="panel3a-header"
                  >
                    <RequestQuoteIcon style={{ marginRight: "15px" }} />
                    <Typography>Loan Details</Typography>
                  </AccordionSummary>

                  <AccordionDetails style={{ paddingBottom: "40px" }}>
                    <LoanDetails asset={asset} loanDetails={loanDetails} />
                  </AccordionDetails>
                </Accordion>

                <Entities application={application} />

                {application?.customers?.length > 0 &&
                  application?.customers?.map((customer, i) => (
                    <Accordion
                      defaultExpanded
                      id={`applicant-${customer.firstName}-${customer.lastName}`}
                      elevation={1}
                      style={{ padding: "0 15px" }}
                      key={customer._id}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel2a-content"
                        id="panel2a-header"
                      >
                        <PersonIcon style={{ marginRight: "15px" }} />
                        <Typography>
                          {customer.firstName || customer.lastName
                            ? `${customer.firstName} ${customer.lastName}`
                            : `[New ${customerRole}]`}
                        </Typography>
                        {!confirmRemoveCustomer[customer._id] && (
                          <Button
                            size="small"
                            style={{
                              padding: 0,
                              marginLeft: "20px",
                            }}
                            onClick={() =>
                              setConfirmRemoveCustomer({ [customer._id]: true })
                            }
                          >
                            Remove
                          </Button>
                        )}
                        {confirmRemoveCustomer[customer._id] && (
                          <Stack direction="row" style={{ marginLeft: "20px" }}>
                            <Typography>Are you sure?</Typography>
                            <Button
                              size="small"
                              style={{
                                padding: 0,
                                marginLeft: "20px",
                              }}
                              onClick={() =>
                                setConfirmRemoveCustomer({
                                  [customer._id]: false,
                                })
                              }
                            >
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              style={{
                                padding: 0,
                                marginLeft: "20px",
                                color: "#d32f2f",
                              }}
                              onClick={() => {
                                dispatch(
                                  removeCustomer({
                                    customerId: customer._id,
                                    applicationId: application._id,
                                  })
                                );
                                setConfirmRemoveCustomer({
                                  [customer._id]: false,
                                });
                              }}
                            >
                              Remove
                            </Button>
                          </Stack>
                        )}
                      </AccordionSummary>

                      <AccordionDetails style={{ paddingBottom: "40px" }}>
                        <Grid container spacing={3}>
                          <CustomerDetails
                            socket={socket}
                            customer={customer}
                            applicationId={application._id}
                            index={i}
                          />
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                <Paper
                  id="addGuarantor"
                  style={{
                    marginTop: "10px",
                    marginBottom: "15px",
                    padding: "15px 15px 15px 35px",
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    cursor: "pointer",
                  }}
                  onClick={handleAddCustomer}
                >
                  <PersonAddAltIcon style={{ marginRight: "10px" }} />
                  <Typography>Add {customerRole}</Typography>
                </Paper>

                {!application?.customers?.[0] && (
                  <Typography
                    variant="caption"
                    style={{
                      padding: "15px 15px 15px 0px",
                    }}
                  >
                    <strong>
                      It is recommended to add an entity before adding
                      customers.
                    </strong>{" "}
                    {customerRole}s are automatically added to the application
                    from ASIC if the entity is a company.
                  </Typography>
                )}

                <Accordion
                  defaultExpanded
                  id="declaration"
                  elevation={1}
                  style={{ padding: "0 15px" }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel2a-content"
                    id="panel2a-header"
                  >
                    Declaration
                  </AccordionSummary>

                  <AccordionDetails style={{ paddingBottom: "40px" }}>
                    <Stack direction="column">
                      <FormControl style={{ marginBottom: "20px" }}>
                        <FormLabel
                          id="demo-radio-buttons-group-label-one"
                          style={firstDeclarationStyle}
                        >
                          Is the proposed security a hire/rental, uber, courier, bus, luxury/exotic car or tier 2 branded asset? (refer to your BDM if unsure)
                        </FormLabel>
                        <RadioGroup
                          aria-labelledby="demo-radio-buttons-group-label-one"
                          value={declarationOne || ""}
                          onClick={(event) => {
                            setDeclarationOne(event.target.value);
                            dispatch(
                              updateDeclaration({
                                _id: declaration._id,
                                one: event.target.value,
                              })
                            );
                          }}
                        >
                          <FormControlLabel
                            value={declarationAnswers.YES}
                            control={<Radio />}
                            label="Yes"
                            style={firstDeclarationStyle}
                          />
                          <FormControlLabel
                            value={declarationAnswers.NO}
                            control={<Radio />}
                            label="No"
                            style={firstDeclarationStyle}
                          />
                        </RadioGroup>
                      </FormControl>

                      <FormControl style={{ marginBottom: "20px" }}>
                        <FormLabel
                          id="demo-radio-buttons-group-label-two"
                          style={secondDeclarationStyle}
                        >
                          Does the client have current ATO arrears or a payment
                          arrangement in place? (refer to your BDM if unsure)
                        </FormLabel>
                        <RadioGroup
                          aria-labelledby="demo-radio-buttons-group-label-two"
                          value={declarationTwo || ""}
                          onClick={(event) => {
                            setDeclarationTwo(event.target.value);
                            dispatch(
                              updateDeclaration({
                                _id: declaration._id,
                                two: event.target.value,
                              })
                            );
                          }}
                        >
                          <FormControlLabel
                            value={declarationAnswers.YES}
                            control={<Radio />}
                            label="Yes"
                            style={secondDeclarationStyle}
                          />
                          <FormControlLabel
                            value={declarationAnswers.NO}
                            control={<Radio />}
                            label="No"
                            style={secondDeclarationStyle}
                          />
                        </RadioGroup>
                      </FormControl>

                      <FormControl style={{ marginBottom: "20px" }}>
                        <FormLabel
                          id="demo-radio-buttons-group-label-three"
                          style={thirdDeclarationStyle}
                        >
                          Does the client have any adverse credit?
                        </FormLabel>
                        <RadioGroup
                          aria-labelledby="demo-radio-buttons-group-label-three"
                          value={declarationThree || ""}
                          onClick={(event) => {
                            setDeclarationThree(event.target.value);
                            dispatch(
                              updateDeclaration({
                                _id: declaration._id,
                                three: event.target.value,
                              })
                            );
                          }}
                        >
                          <FormControlLabel
                            value={declarationAnswers.YES}
                            control={<Radio />}
                            label="Yes"
                            style={thirdDeclarationStyle}
                          />
                          <FormControlLabel
                            value={declarationAnswers.NO}
                            control={<Radio />}
                            label="No"
                            style={thirdDeclarationStyle}
                          />
                        </RadioGroup>
                      </FormControl>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Grid>

            <Grid
              container
              item
              sm={3}
              md={3}
              lg={3}
              style={{ padding: "0 20px" }}
            >
              <Box
                style={{
                  width: "calc(25vw - 40px)",
                  position: "fixed",
                  overflowY: "auto",
                  overflowX: "hidden",
                  height: "calc(100vh - 140px)",
                  scrollbarColor: "white rebeccapurple",
                  scrollbarWidth: "thin",
                }}
              >
                <ProductSelector
                  isApplicationComplete={isApplicationComplete}
                />
              </Box>
            </Grid>
            <Snackbar
              open={submitSuccess}
              autoHideDuration={8000}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              onClose={() => dispatch(saveValue({ submitSuccess: false }))}
            >
              <Alert
                onClose={() => dispatch(saveValue({ submitSuccess: false }))}
                elevation={6}
                severity="success"
                sx={{ width: "100%" }}
              >
                Application submitted successfully
              </Alert>
            </Snackbar>
            <Snackbar
              open={linkSent}
              autoHideDuration={8000}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              onClose={() => dispatch(saveValue({ linkSent: false }))}
            >
              <Alert
                onClose={() => dispatch(saveValue({ linkSent: false }))}
                elevation={6}
                severity="success"
                sx={{ width: "100%" }}
              >
                Link sent
              </Alert>
            </Snackbar>

            <Snackbar
              open={gotASICData}
              autoHideDuration={8000}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              onClose={() => dispatch(saveValue({ linkSent: false }))}
            >
              <Alert
                onClose={() => dispatch(saveValue({ linkSent: false }))}
                elevation={6}
                severity="success"
                sx={{ width: "100%" }}
              >
                ASIC Data retrieved
              </Alert>
            </Snackbar>
          </Grid>

          <ConfirmModal
            isOpen={removeAssetModalOptions?.isOpen}
            handle={handle.removeAsset}
            setIsOpen={handle.closeRemoveAssetModal}
            title={"confirm"}
            btnTextNo="No"
            btnTextYes="Yes"
            confirmLoadingText="Removing..."
            message={`You’re about to remove asset ${
              removeAssetModalOptions?.assetType
                ? `“${removeAssetModalOptions.assetType}”`
                : ""
            }. Are you sure?`}
            isConfirmLoading={removeAssetModalOptions?.isRemovingAsset}
          />
        </>
      )}
    </>
  );
});

export default Application;
